import type { KeywordOrigin } from "../../components/shared/origin-chips";
import { apiPost, apiPut } from "../fetch";
import { getGlobalHeadersAsObject } from "../global-headers";
import { GradeConnectTypeExtended } from "../grade_connect_queue/grade-connect-queue";
import type { DRFListResponseWrapper } from "../types";

export type AddVote = {
	grade: string;
	user: number;
	person: number;
	assignment: number;
	vote_type?: string;
	sub_type?: string;
	method?: string;
};

export type Vote2 = {
	grade: string;
	vote_type: string;
	sub_type?: string;
	/** The grading-queue row voted on; null for a vote cast outside grading,
	 *  such as from the candidate pool, where there is no queue row. */
	properties: GradeConnectTypeExtended | null;
	mode: number | null;
	person: number;
	user: number;
	assignment: number;
	person_name?: string;
	person_headline?: string;
	user_username?: string;
	assignment_name?: string;
	source_label?: string | null;
	source_kind?: string | null;
	mode_label?: string | null;
	/** Recruiter-approved AI-drafted pitch text (set when the unified draft-
	 *  pitch dialog confirms an up-vote). Used by the comm matrix at dispatch
	 *  time as an override for the standard template render. */
	/** Graded, but the applicant is told nothing. Set from the reply dialog,
	 *  where the grader is looking at the text that would otherwise be sent.
	 *  Meaningless for a sourced candidate — it only suppresses the applicant's
	 *  own reply, never a pitch. */
	applicant_reply_suppressed?: boolean;
	pitch_text?: string | null;
	/** Email-only subject companion to ``pitch_text``; ignored on LinkedIn. */
	pitch_subject?: string | null;
	/** Gate for the pitch_text override: ``"email"`` / ``"linkedin"`` for warm
	 *  cases (override only used when the matrix re-dispatches on THAT same
	 *  channel — stops a chat-y LinkedIn note from leaking out as cold email),
	 *  ``null`` for cold cases (recruiter explicitly opted into "use my text
	 *  whichever channel you pick"). */
	pitch_channel?: "email" | "linkedin" | null;
	/** The colleague whose conversation a warm pitch continues, who sends it;
	 *  null lets the matrix continue the newest conversation on the channel. */
	pitch_sender?: number | null;
	/** The EmailJournal a warm email pitch answers, in its own thread. */
	pitch_reply_to?: number | null;
	/** Which tab of the draft-pitch dialog wrote `pitch_text`. The matrix follows
	 *  up on a template pitch and stops after an AI or manual one, so it cannot
	 *  be derived from `llm_prompt_template`: that is null for both a kept
	 *  template render and a hand-typed note, which want opposite behaviour. */
	text_origin?: "template" | "ai" | "manual" | null;
	/** Prompt version that drafted `pitch_text`. The matrix copies it onto the
	 *  message it dispatches, so an AI-written pitch says so even when it rides
	 *  a template's slot in the follow-up chain. */
	llm_prompt_template?: number | null;
	/** Whether the recruiter rewrote the draft before approving it. */
	is_text_edited?: boolean;
	/** What the dialog suggested before the recruiter edited it — the AI draft
	 *  or the rendered template, ``null`` when sent from the Manual tab. Kept
	 *  beside `pitch_text` so the edit itself can be read, not just its
	 *  existence. */
	pitch_suggested_text?: string | null;
	pitch_suggested_subject?: string | null;
	/** The documents this one pitch carries; `pitch_documents_edited` is what
	 *  makes an empty list mean "the recruiter removed them all". */
	pitch_documents?: number[];
	pitch_documents_edited?: boolean;
};

export interface ResponseVote2 extends Omit<Vote2, "properties"> {
	id: number;
	created: string;
	/** The grading-queue row as it stood when the vote was cast — where a
	 *  graded person's band comes from once their queue rows are dequeued.
	 *  Null on rows written before the snapshot was stored (118 425 of the
	 *  312 560 votes in production), so a reader must handle its absence;
	 *  every vote this app writes carries one. */
	properties: GradeConnectTypeExtended | null;
}

/** Rejects with `ApiHttpError` on a non-2xx answer (a deploy-window 502
 * included), so the caller's `.catch` reports the lost vote. */
export const createVote2 = (vote: Vote2): Promise<ResponseVote2> =>
	apiPost("/api/v2/vote2/", vote) as Promise<ResponseVote2>;

export const updateVote2 = (vote: Vote2, voteId: number): Promise<ResponseVote2> =>
	apiPut(`/api/v2/vote2/${voteId}/`, vote) as Promise<ResponseVote2>;

export const getVotes = (params: {
	assignment?: number;
	mode?: number;
	limit?: number;
	offset?: number;
	grade?: string;
	person?: number;
	ordering?: string;
}): Promise<DRFListResponseWrapper<ResponseVote2[]>> => {
	const query: string[][] = [];
	if (params.assignment !== undefined)
		query.push(["assignment", String(params.assignment)]);
	if (params.mode !== undefined) query.push(["mode", String(params.mode)]);
	if (params.limit !== undefined) query.push(["limit", String(params.limit)]);
	if (params.offset !== undefined) query.push(["offset", String(params.offset)]);
	if (params.grade !== undefined) query.push(["grade", params.grade]);
	if (params.person !== undefined) query.push(["person__id", String(params.person)]);
	if (params.ordering !== undefined) query.push(["ordering", params.ordering]);
	const qs = new URLSearchParams(query).toString();
	const requestUrl = `/api/v2/vote2/${qs ? `?${qs}` : ""}`;
	return fetch(requestUrl, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export interface VotesBucket {
	up: number;
	total: number;
}

export interface VotesBucketSet {
	llm_yes: VotesBucket;
	llm_no: VotesBucket;
	other: VotesBucket;
}

export interface VotesUserBucket extends VotesBucketSet {
	user_id: number;
	username: string;
	first_name: string;
}

export interface VotesBucketSetWithUsers extends VotesBucketSet {
	users: VotesUserBucket[];
	first_vote_at: string | null;
	last_vote_at: string | null;
}

export type AttributionKind = "keyword" | "bq";

export interface VotesAttributionBucket extends VotesBucketSetWithUsers {
	key: string;
	kind: AttributionKind;
	label: string;
	gcq_mode: number | null;
	gcq_mode_label: string | null;
	keyword: string | null;
	source: string | null;
	run_id: number | null;
	/** How the term got into the search list, from the row snapshot this vote
	 * froze. Empty on a BQ bucket and on a keyword vote stored before the
	 * origins were recorded. */
	origins: KeywordOrigin[];
}

export interface VotesAttributionGroup extends VotesBucketSetWithUsers {
	gcq_mode: number | null;
	gcq_mode_label: string | null;
	buckets: VotesAttributionBucket[];
}

export interface VotesBySourceResponse {
	assignment_id: number;
	total: VotesBucketSetWithUsers;
	none_bucket: VotesBucketSetWithUsers;
	groups: VotesAttributionGroup[];
	distinct_voters: number;
}

export const getVotesBySource = async (
	assignmentId: number
): Promise<VotesBySourceResponse> => {
	const requestUrl = `/api/v2/vote2/by_source/?assignment=${assignmentId}`;
	const res = await fetch(requestUrl, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	return (await res.json()) as VotesBySourceResponse;
};

/** What undoing a vote could not take back — an email already sent to the
 *  applicant, or a stage they have already been moved to in the customer's
 *  ATS. Empty for every sourced candidate. */
export interface DeletedVoteResult {
	deleted: number;
	warnings?: string[];
}

export const deleteVote2 = async (
	voteId: number
): Promise<DeletedVoteResult | void> => {
	const requestUrl = `/api/v2/vote2/${voteId}/`;
	const res = await fetch(requestUrl, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	});
	if (res.status === 204) return;
	try {
		return (await res.json()) as DeletedVoteResult;
	} catch (_e) {
		return;
	}
};
